import { productInfo } from '../AvProSwitcher';
export class AvProSwitcher {
    constructor(socket, log) {
        this.socket = socket;
        this.audioOutputs = Array(17).fill({
            volume: 10,
            mute: false,
        });
        this.audioMatrix = Array(17).fill('1');
        this.avAddress = '00';
        this.micOutputs = Array(2).fill({
            volume: 10,
            mute: false,
        });
        this.firmwareVersion = '2.34';
        this.numAudioOutputs = 0; // not all products have audio outputs
        this.numVideoInputs = 4;
        this.numVideoOutputs = 4;
        this.videoOutputs = Array(17).fill('1');
        this.asyncHandlers = [
            {
                name: 'address',
                regExp: /GET\s+ADDR/i,
                callback: (match) => {
                    this.socket.send(`ADDR ${this.avAddress}`);
                },
            },
            {
                name: 'audioSwitchSet',
                regExp: /SET OUT(\d+)\s+AS\s+IN(\d+)/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const audInput = match[2];
                    if (outputNum === 0) {
                        this.audioMatrix.fill(audInput); // set for all outputs
                    }
                    else {
                        this.audioMatrix[outputNum] = match[2];
                    }
                    this.socket.send(`OUT${outputNum} AS IN${audInput}`);
                },
            },
            {
                name: 'audioSwitchGet',
                regExp: /GET OUT(\d+)\s+AS/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    if (outputNum === 0) {
                        for (let i = 1; i <= this.numAudioOutputs; i++) {
                            this.socket.send(`OUT${i} AS IN${this.audioMatrix[i]}`);
                        }
                    }
                    else {
                        this.socket.send(`OUT${outputNum} AS IN${this.audioMatrix[outputNum]}`);
                    }
                },
            },
            {
                name: 'muteQuery',
                regExp: /GET OUT(\d+)\s+EXA$/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const mute = this.audioOutputs[outputNum].mute;
                    if (outputNum === 0) {
                        for (let i = 1; i <= this.numAudioOutputs; i++) {
                            this.socket.send(`OUT${i} EXA ${this.audioOutputs[i].mute ? 'DIS' : 'EN'}`);
                        }
                    }
                    else {
                        this.socket.send(`OUT${outputNum} EXA ${mute ? 'DIS' : 'EN'}`);
                    }
                },
            },
            {
                name: 'muteQueryMic',
                regExp: /GET OUT(\d+)\s+EXA MIC/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const mute = this.micOutputs[outputNum].mute;
                    this.socket.send(`OUT${outputNum} EXA MIC ${mute ? 'DIS' : 'EN'}`);
                },
            },
            {
                name: 'muteSet',
                regExp: /SET OUT(\d+)\s+EXA\s+(EN|DIS)/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const mute = match[2].toUpperCase() === 'DIS';
                    this.audioOutputs[outputNum].mute = mute;
                    if (outputNum === 0) {
                        // set for all outputs
                        this.audioOutputs.forEach(out => {
                            out.mute = mute;
                        });
                    }
                    this.socket.send(`OUT${outputNum} EXA ${mute ? 'DIS' : 'EN'}`);
                },
            },
            {
                name: 'muteSetMic',
                regExp: /SET OUT(\d+)\s+EXA\s+MIC\s+(EN|DIS)/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const mute = match[2].toUpperCase() === 'DIS';
                    this.micOutputs[outputNum].mute = mute;
                    if (outputNum === 0) {
                        // set for all outputs
                        this.micOutputs.forEach(chan => {
                            chan.mute = mute;
                        });
                    }
                    this.socket.send(`OUT${outputNum} EXA MIC ${mute ? 'DIS' : 'EN'}`);
                },
            },
            {
                name: 'videoSwitch',
                regExp: /SET OUT(\d+)\s+VS\s+IN(\d+)/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const vidInput = match[2];
                    if (outputNum === 0) {
                        // set for all outputs
                        this.videoOutputs.fill(vidInput);
                    }
                    this.videoOutputs[outputNum] = match[2] ? match[2] : '1';
                    this.socket.send(`OUT${outputNum} VS IN${vidInput}`);
                },
            },
            {
                name: 'VideoSwitchGet',
                regExp: /GET OUT(\d+)\s+VS/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    if (outputNum === 0) {
                        for (let i = 1; i <= this.numVideoOutputs; i++) {
                            this.socket.send(`OUT${i} VS IN${this.videoOutputs[i]}`);
                        }
                    }
                    else {
                        this.socket.send(`OUT${outputNum} VS IN${this.videoOutputs[outputNum]}`);
                    }
                },
            },
            {
                name: 'statusQuery',
                regExp: /^STA$/i,
                callback: (match) => {
                    const response = this.createStatusMessage();
                    response.forEach(resp => {
                        this.socket.send(resp);
                    });
                },
            },
            {
                name: 'volumeQuery',
                regExp: /GET OUT(\d+)\s+EXAUD LEV/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const volume = this.audioOutputs[outputNum].volume;
                    if (outputNum === 0) {
                        for (let i = 1; i <= this.numAudioOutputs; i++) {
                            this.socket.send(`OUT${i} EXAUD LEV${this.audioOutputs[i].volume}`);
                        }
                    }
                    else {
                        this.socket.send(`OUT${outputNum} EXAUD LEV${volume}`);
                    }
                },
            },
            {
                name: 'volumeSet',
                regExp: /SET OUT(\d+)\s+EXAUD\s+LEV\s*(\d+)/i,
                callback: (match) => {
                    const outputNum = parseInt(match[1], 10);
                    const volume = parseInt(match[2], 10);
                    this.audioOutputs[outputNum].volume = volume;
                    if (outputNum === 0) {
                        // set for all outputs
                        this.audioOutputs.forEach(out => {
                            out.volume = volume;
                        });
                    }
                    this.socket.send(`OUT${outputNum} EXAUD LEV ${volume}`);
                },
            },
            {
                name: 'invalid',
                regExp: /.+/i,
                callback: (match) => {
                    this.socket.send(`CMD ERR`);
                },
            },
        ];
    }
    onConfigure(config) {
        var _a, _b, _c, _d, _e;
        const modelNumber = (_a = config.modelNumber) !== null && _a !== void 0 ? _a : '-NBIWNYBTBvKlqhEFrpn';
        this.numAudioOutputs = (_c = (_b = productInfo[modelNumber]) === null || _b === void 0 ? void 0 : _b.numAudioOutputs) !== null && _c !== void 0 ? _c : 0;
        // Don't include the 'all' output as a separate output
        this.numVideoOutputs = Object.values((_d = config.videoOutputs) !== null && _d !== void 0 ? _d : {}).filter(out => out.code !== '0').length;
        this.numVideoInputs = Object.values((_e = config.videoInputs) !== null && _e !== void 0 ? _e : {}).length;
    }
    createStatusMessage() {
        const out = [];
        out.push('==============================================================================================================================');
        out.push('=*******************************************************Systems STATUS********************************************************=');
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push(`=                        System Address = ${this.avAddress}          F/W Version : ${this.firmwareVersion}                                                     =`);
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push('= System Setup Status :                                                                                                       =');
        out.push('= LCD Remain ON: 30Sec                                                                                                        =');
        out.push('= KEY LOCK: OFF                                                                                                               =');
        out.push('= EX-Audio Mode: Bind To Output                                                                                               =');
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push('= Network Setup(Telnet Server) Status:                                                                                        =');
        out.push('= MAC Address = 18:8a:6a:00:60:d1                                                                                             =');
        out.push('= Host IP Address = 172.017.103.170                                                                                           =');
        out.push('= Net Mask = 255.255.252.000                                                                                                  =');
        out.push('= Router IP Address = 172.017.100.001                                                                                         =');
        out.push('= TCP Port = 0023                                                                                                             =');
        out.push('= DHCP = OFF                                                                                                                  =');
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push('= Input Setup Status :                                                                                                        =');
        for (let i = 1; i <= this.numVideoInputs; i++) {
            out.push(`= IN${i}: EDID = 1080P_2CH            , Link = ON , HDCP = NONE,                                                                 =`);
        }
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push('= Output Setup Status:                                                                                                        =');
        for (let i = 1; i <= this.numVideoOutputs; i++) {
            out.push(`= OUT${i}: Input = ${this.videoOutputs[i]}, EX-Audio =Enable  , EX-ADL =BYPASS, EX-Audio =In${this.videoOutputs[i]}                                                          =`);
        }
        out.push('= HDMI Output Setup Status:                                                                                                   =');
        for (let i = 1; i <= this.numVideoOutputs; i++) {
            out.push(`= OUT${i}: Video Mode = BYPASS  , Link = ON , OutStream = ON , Test Pattern = Disable                                            =`);
        }
        out.push('= HDBT Output Setup Status:                                                                                                   =');
        for (let i = 1; i <= this.numVideoOutputs; i++) {
            out.push(`= OUT${i}: Video Mode = ICT Mode, Link = ON , OutStream = ON , Test Pattern = Disable                                            =`);
        }
        out.push('=-----------------------------------------------------------------------------------------------------------------------------=');
        out.push('=*****************************************************************************************************************************=');
        out.push('===============================================================================================================================');
        return out;
    }
}
AvProSwitcher.emulatorType = 'socketEmulator';
AvProSwitcher.key = '-LrVMuQ4zs1tpH0dKEvC';
